import {
  type ClientEvent,
  type ClientEventPayload,
  ClientTrackEventRequest,
} from "@quotientjs/core"
import type { AnalyticsSession } from "@/analytics/session"
import type { BrowserApiClient } from "@/browser-api-client"
import type { Store } from "@/clients/store"

// Share this state across all analytics clients on the page so browser history
// is only patched once. A new client replaces the active handler instead of
// adding another wrapper, which prevents duplicate tracking after navigation.
// lastTrackedPageKey lives here too, not on the instance, so dedup still works
// if the SDK ends up mounted twice or the client gets re-initialized.
let historyPatched = false
let activeNavigationHandler: (() => void) | null = null
let lastTrackedPageKey: string | undefined

function ensureHistoryPatched(): void {
  if (historyPatched) {
    return
  }
  historyPatched = true

  const dispatch = (): void => {
    activeNavigationHandler?.()
  }

  window.addEventListener("popstate", dispatch)

  const pushState = window.history.pushState.bind(window.history)
  window.history.pushState = (...args: Parameters<History["pushState"]>) => {
    pushState(...args)
    dispatch()
  }

  const replaceState = window.history.replaceState.bind(window.history)
  window.history.replaceState = (
    ...args: Parameters<History["replaceState"]>
  ) => {
    replaceState(...args)
    dispatch()
  }
}

export class Analytics {
  private apiClient: BrowserApiClient
  private session: AnalyticsSession
  private store: Store
  private pageViewTrackingInitialized = false
  private navigationHandler: (() => void) | null = null

  constructor(
    apiClient: BrowserApiClient,
    session: AnalyticsSession,
    store: Store,
  ) {
    this.apiClient = apiClient
    this.session = session
    this.store = store
  }

  /**
   * Sends an analytics event with the current session, attribution, and
   * browser details.
   */
  async event(event: ClientEvent): Promise<void> {
    await this.trackCurrentPage(event, { deduplicate: false })
  }

  /**
   * Starts automatic page-view tracking. It records the current page, then
   * tracks future browser navigation. `QuotientClient.init()` calls this once.
   */
  initPageViewTracking(): void {
    if (this.pageViewTrackingInitialized) {
      return
    }
    this.pageViewTrackingInitialized = true

    ensureHistoryPatched()
    this.navigationHandler = (): void => {
      void this.trackCurrentPage(
        { eventType: "pageView" },
        { deduplicate: true },
      )
    }
    activeNavigationHandler = this.navigationHandler

    void this.trackCurrentPage({ eventType: "pageView" }, { deduplicate: true })
  }

  /**
   * Stops this client from tracking browser navigation. Call this when the
   * client is no longer in use, such as when its React provider unmounts.
   */
  disablePageViewTracking(): void {
    if (activeNavigationHandler === this.navigationHandler) {
      activeNavigationHandler = null
    }
    this.navigationHandler = null
    this.pageViewTrackingInitialized = false
  }

  /**
   * Sends an event for the current page. Automatic page views are sent only
   * once per URL, while events sent through `event()` are always recorded.
   */
  private async trackCurrentPage(
    event: ClientEvent,
    { deduplicate }: { deduplicate: boolean },
  ): Promise<void> {
    const pageKey = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (deduplicate && pageKey === lastTrackedPageKey) {
      return
    }
    lastTrackedPageKey = pageKey

    this.session.prepare()
    this.apiClient.setAnalyticsSessionId(this.session.id)
    this.apiClient.setReferrerUrl(this.session.referrerUrl ?? null)

    // A tagged person ID must remain available after its attribution expires,
    // but a restored (not freshly tagged) attribution shouldn't undo a more
    // authoritative person.upsert() call made since the last page view.
    if (
      this.session.attribution.personId &&
      (this.session.attributionIsFresh || !this.store.personId())
    ) {
      this.store.setPersonId(this.session.attribution.personId)
    }
    const personId = this.store.personId() ?? undefined
    const payload: ClientEventPayload = {
      ...this.session.attribution,
      ...(personId ? { personId } : {}),
      ...event,
      pathname: window.location.pathname,
      pageUrl: window.location.href,
    }

    await this.apiClient.call(new ClientTrackEventRequest(payload))
  }
}
